import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton } from '~/components/isolated/IconButton';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockTitle } from '../shared/CategoryBlockTitle';
import { createCategoryUrl } from '../shared/utils';

interface CategoriesCarouselSliderOneBlockProps {
  category: Category;
}

const ICON_BUTTON_OVERRIDES =
  'border-stroke-inverse-primary! text-text-primary-inverse! [&_svg]:text-text-primary-inverse! hover:bg-surface-inverse-transparent-2!';

const CategoriesCarouselSliderOneBlock = ({
  category,
}: CategoriesCarouselSliderOneBlockProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { handleClickCategory } = useCategoryAnalytics();

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  if (!category.subcategory.length) {
    return null;
  }

  const ViewAllComponent = isMobile
    ? Typography.Text100Medium
    : Typography.Text200Medium;

  const CardTitleComponent = isMobile
    ? Typography.Header150Medium
    : Typography.Header300Medium;

  const renderCard = (item: Category['subcategory'][number]) => (
    <Link
      to={createCategoryUrl({ id: item.id })}
      onClick={handleClickCategory(item.id)}
      data-link={`category-${item.id}`}
      className="relative flex aspect-166/169 w-full overflow-hidden rounded-5.5 no-underline md:rounded-6.5"
    >
      <CloudflareResponseImage
        src={item.previewImage || ''}
        alt={item.name}
        className="absolute inset-0 size-full object-cover"
        loading="lazy"
        srcSetParams={defaultSrcSetParams}
      />
      <div className="absolute inset-0 bg-linear-to-b from-black/20 to-black/40" />
      <div className="absolute right-4 bottom-4 left-4 md:right-9 md:bottom-7 md:left-7">
        <CardTitleComponent
          as="span"
          className="wrap-anywhere block text-text-primary-inverse!"
        >
          {item.name}
        </CardTitleComponent>
      </div>
    </Link>
  );

  const viewAllLinkTo =
    pathname !== '/search' ? createCategoryUrl({ id: category.id }) : undefined;

  const showArrows = !isMobile && (!prevBtnDisabled || !nextBtnDisabled);

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 rounded-6 bg-graphic-regular-brand p-4',
        'md:gap-8 md:rounded-[40px] md:p-8',
      )}
    >
      <header className="flex w-full items-center gap-3 md:gap-4">
        <CategoryBlockTitle inverse className="wrap-anywhere flex-1">
          {category.name}
        </CategoryBlockTitle>

        <div className="flex shrink-0 items-center gap-2">
          {viewAllLinkTo && (
            <Link
              to={viewAllLinkTo}
              className={cn(
                'inline-flex h-8 items-center rounded-full border border-stroke-inverse-primary! px-3 text-text-primary-inverse! no-underline transition-colors',
                'hover:bg-surface-inverse-transparent-2! hover:text-text-primary-inverse! hover:no-underline',
                'md:h-10 md:px-4',
              )}
              onClick={handleClickCategory(category.id)}
            >
              <ViewAllComponent as="span" className="block">
                {t('viewAll')}
              </ViewAllComponent>
            </Link>
          )}

          {showArrows && (
            <div className="flex items-center gap-2">
              <IconButton
                icon="chevron-3.1"
                iconSize={14}
                size="M"
                variant="outline"
                disabled={prevBtnDisabled}
                className={ICON_BUTTON_OVERRIDES}
                onClick={onPrevButtonClick}
              />
              <IconButton
                icon="chevron-3.1"
                iconSize={14}
                size="M"
                variant="outline"
                disabled={nextBtnDisabled}
                className={cn(ICON_BUTTON_OVERRIDES, '[&>svg]:rotate-180')}
                onClick={onNextButtonClick}
              />
            </div>
          )}
        </div>
      </header>

      {!isMobile && (
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: 1,
            containScroll: 'trimSnaps',
            dragFree: false,
          }}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent>
            {category.subcategory.map((item) => (
              <CarouselItem
                key={item.id}
                className="mr-4 basis-[calc((100%-48px)/4)]! pl-0! last:mr-0"
              >
                {renderCard(item)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {isMobile && (
        <div
          className={cn(
            '-mx-4 flex max-w-[calc(100%+32px)] gap-2 overflow-x-auto overflow-y-hidden px-4 pb-2',
            '[-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none]',
            '[&::-webkit-scrollbar]:hidden',
          )}
        >
          {category.subcategory.map((item) => (
            <div key={item.id} className="flex w-70 shrink-0">
              {renderCard(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default observer(CategoriesCarouselSliderOneBlock);
